import cx from 'classnames'
import Styles from 'components/WorkspaceDashboard/WorkspaceDashboard.module.scss'
import { UpdateBlazePaymentMethodModal } from 'components/UpdateBlazePaymentMethodModal/UpdateBlazePaymentMethodModal'
import { WorkspaceDashboardSidebar } from 'blaze-components/WorkspaceDashboardSidebar/WorkspaceDashboardSidebar'
import { Container } from 'components/WorkspaceDashboard/components/Container/Container'
import { RootState, Workspace } from 'types'
import SettingsContainer from 'components/Settings/SettingsContainer'
import { PropsWithChildren, useEffect, useRef } from 'react'
import { useUnregisteredUserBanner } from 'hooks/useUnregisteredUser'
import { useSuperAdmin } from 'hooks/useSuperAdmin'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { useRootStore } from 'stores/RootStore'
import { useLocation } from 'react-router-dom'
import { useLongHover } from 'hooks/useLongHover'
import { setFloatingSidebar } from 'redux/actions'
import { useShowFloatingOrgSidebar } from 'hooks/useShowFloatingOrgSidebar'
import { TopBar as NewTopBar } from 'blaze-components/TopBar/TopBar'
import { useSidebarState } from 'blaze-components/WorkspaceDashboardSidebar/hooks/useSidebarState'
import { observer } from 'mobx-react-lite'
import { useLayout } from 'stores/LayoutStore'
import { useAnnualTrialDiscountBanner } from 'hooks/useAnnualTrialDiscountBanner/useAnnualTrialDiscountBanner'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import BrandKitDashboardStyles from './BrandKitDashboard.module.scss'

interface Props {
  workspace: Workspace
}

export const BrandKitDashboard = observer(({ children, workspace }: PropsWithChildren<Props>) => {
  const mainContainerRef = useRef<HTMLDivElement>(null)
  const [showUnregisteredUserBanner] = useUnregisteredUserBanner()
  const { superAdminMode } = useSuperAdmin()
  const dispatch = useDispatch()
  const location = useLocation()
  const { leftSidebarStore, workspaceTheme } = useRootStore()
  const sidebarState = useSidebarState()
  const floatingSidebar = useSelector(({ floatingSidebar }: RootState) => floatingSidebar, isEqual)
  const { setShowFloatingOrgSidebar } = useShowFloatingOrgSidebar()
  const layout = useLayout()
  const { annualTrialDiscountBanner, showAnnualTrialDiscountBanner } = useAnnualTrialDiscountBanner()

  useEffect(() => {
    if (layout.dashboardContent) {
      layout.dashboardContent.scrollTop = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useLongHover(
    mainContainerRef,
    () => {
      if (floatingSidebar) {
        setShowFloatingOrgSidebar(false)
        dispatch(setFloatingSidebar(false))
      }
    },
    () => null,
    1,
  )

  return (
    <div
      className={cx(Styles.dashboard, BrandKitDashboardStyles.dashboard, {
        [Styles.withAnnualTrialDiscountBanner]: showAnnualTrialDiscountBanner,
        [Styles.withUnregisteredBanner]: showUnregisteredUserBanner,
        [Styles.inSuperAdminMode]: superAdminMode,
      })}
      id="workspace-dashboard"
      style={{
        ...workspaceTheme.paletteProperties(['--sidebar-background-color']),
      }}
    >
      {showAnnualTrialDiscountBanner && annualTrialDiscountBanner}
      {workspace?.paying && workspace?.payment_failed && <UpdateBlazePaymentMethodModal />}
      {sidebarState.isVisible && (
        <ResponsiveDesign.Desktop>
          <WorkspaceDashboardSidebar />
        </ResponsiveDesign.Desktop>
      )}
      <div className={Styles.innerContent}>
        <NewTopBar isLockable={false} />
        <Container
          className={cx(Styles.mainContainer, Styles.notCore, Styles.notEditing, BrandKitDashboardStyles.container)}
          ref={mainContainerRef}
        >
          <div
            className={Styles.contentWrapper}
            id="dashboard-content-wrapper"
            ref={(node) => layout.setDashboardContent(node)}
            onMouseEnter={() => leftSidebarStore.setIsHovered(false)}
          >
            {children}
            <SettingsContainer workspace={workspace} />
          </div>
        </Container>
      </div>
    </div>
  )
})
